import { useMemo, useState } from 'react';
import type {
  ModelPlanDetail,
  ModelPlanPropagationRunSummary,
  ModelPlanSkuInput,
  ModelPlanUnsyncedSku,
  UpdateModelPlanRequest,
} from '@nadoutong/contracts';
import { normalizeBrandId } from '@nadoutong/contracts';
import { Eye, EyeOff } from 'lucide-react';
import {
  useModelPlan,
  useModelPlanPropagationRuns,
  useModelPlanTargets,
  useModelPlanUnsyncedModels,
  useRollbackModelPlan,
} from '../../api/modelPlans.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import {
  MODEL_PLAN_MANAGE_ROLES,
  hasAnyOpsRole,
} from '../../auth/opsRoles.ts';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  cx,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  Skeleton,
} from '../../ui/index.ts';
import {
  fmtRate,
  liveBreachesFromResolved,
  parsePositiveRate,
  resolvePlanSkuRates,
} from './floorBreaches.ts';
import type { PlanFloorBreach, SkuResolvedRate } from './floorBreaches.ts';
import { FloorBreachesList, floorHintText, PropagationRunSummaryView } from './preview.tsx';

export function PlanDetailDrawer({
  planId,
  onClose,
  onSync,
}: {
  readonly planId: string;
  readonly onClose: () => void;
  readonly onSync: (
    plan: ModelPlanDetail,
    admitted: readonly ModelPlanSkuInput[],
    resolvedSkus: readonly SkuResolvedRate[],
  ) => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const canManage = hasAnyOpsRole(user?.roles ?? [], MODEL_PLAN_MANAGE_ROLES);
  const detail = useModelPlan(planId, true);
  const runs = useModelPlanPropagationRuns(planId, true);
  const unsynced = useModelPlanUnsyncedModels(planId, true);
  const rollback = useRollbackModelPlan();
  const [pendingRollback, setPendingRollback] = useState<ModelPlanPropagationRunSummary | null>(
    null,
  );
  const [syncOpen, setSyncOpen] = useState(false);
  const [lastRun, setLastRun] = useState<ModelPlanPropagationRunSummary | null>(null);

  const plan = detail.data?.model_plan ?? null;
  const unsyncedCount = unsynced.data?.skus.length ?? 0;

  return (
    <Drawer
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('model_plans.detail.title', { name: plan?.name ?? planId })}
      closeLabel={t('common.close')}
      width="lg"
      testId="model-plans-detail-drawer"
      footer={
        unsyncedCount > 0 ? (
          <>
            <Chip tone="warn" testId="model-plans-detail-new">
              {t('model_plans.unsynced_badge', { count: unsyncedCount })}
            </Chip>
            {canManage ? (
              <Button
                variant="secondary"
                onClick={() => setSyncOpen(true)}
                data-testid="model-plans-detail-sync"
              >
                {t('model_plans.action.sync')}
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      <div className="flex flex-col gap-6">
        {detail.isLoading ? <Skeleton className="h-16" /> : null}
        {detail.isError ? (
          <EmptyState
            title={t('model_plans.error.load')}
            hint={coerceErrorMessage(detail.error)}
            testId="model-plans-detail-error"
          />
        ) : null}

        {lastRun !== null ? (
          <PropagationRunSummaryView run={lastRun} testId="model-plans-detail-last-run" />
        ) : null}

        <section className="flex flex-col gap-3">
          <h3 className="m-0 text-section text-paper">{t('model_plans.detail.runs')}</h3>
          {runs.isLoading ? <Skeleton className="h-24" /> : null}
          {runs.isError ? (
            <p className="m-0 text-dense text-bad" role="alert">
              {t('model_plans.detail.runs_error')}
            </p>
          ) : null}
          {runs.isSuccess && runs.data.runs.length === 0 ? (
            <p className="m-0 text-dense text-paper-low" data-testid="model-plans-runs-empty">
              {t('model_plans.detail.runs_empty')}
            </p>
          ) : null}
          {runs.data?.runs.map((run) => (
            <div
              key={run.id}
              className="flex flex-col gap-2 rounded-lg border border-line-soft bg-well px-3 py-3"
              data-testid={`model-plans-run-${run.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <PropagationRunSummaryView run={run} testId={`model-plans-run-summary-${run.id}`} />
                <span className="text-micro text-paper-low">
                  {formatLocalTimestamp(run.started_at)}
                </span>
              </div>
              {canManage ? (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setPendingRollback(run)}
                  data-testid={`model-plans-run-rollback-${run.id}`}
                >
                  {t('model_plans.detail.rollback')}
                </Button>
              ) : null}
            </div>
          ))}
        </section>
      </div>

      {syncOpen && plan !== null && unsynced.data !== undefined ? (
        <SyncUnsyncedDialog
          plan={plan}
          unsynced={unsynced.data.skus}
          onClose={() => setSyncOpen(false)}
          onSubmit={(admitted, resolvedSkus) => {
            setSyncOpen(false);
            onSync(plan, admitted, resolvedSkus);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={pendingRollback !== null}
        onOpenChange={(open) => {
          if (!open && !rollback.isPending) setPendingRollback(null);
        }}
        title={t('model_plans.detail.rollback_title')}
        body={t('model_plans.detail.rollback_body')}
        confirmLabel={t('model_plans.detail.rollback_confirm')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        tone="danger"
        loading={rollback.isPending}
        onConfirm={() => {
          if (pendingRollback === null) return;
          rollback.mutate(
            { id: planId, runId: pendingRollback.id },
            {
              onSuccess: (response) => {
                setPendingRollback(null);
                setLastRun(response.run);
                toast.success(t('model_plans.toast.rolled_back'));
              },
              onError: (err) => {
                toast.error(coerceErrorMessage(err));
              },
            },
          );
        }}
        testId="model-plans-rollback-confirm"
      />
    </Drawer>
  );
}

interface AdmitDraft {
  readonly sku: ModelPlanUnsyncedSku;
  include: boolean;
  visible: boolean;
  rate: string;
}

function SyncUnsyncedDialog({
  plan,
  unsynced,
  onClose,
  onSubmit,
}: {
  readonly plan: ModelPlanDetail;
  readonly unsynced: readonly ModelPlanUnsyncedSku[];
  readonly onClose: () => void;
  readonly onSubmit: (
    admitted: readonly ModelPlanSkuInput[],
    resolvedSkus: readonly SkuResolvedRate[],
  ) => void;
}): JSX.Element {
  const t = useT();
  const [rows, setRows] = useState<AdmitDraft[]>(() =>
    unsynced.map((sku) => ({ sku, include: false, visible: false, rate: '1' })),
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [blockingBreaches, setBlockingBreaches] = useState<readonly PlanFloorBreach[] | null>(
    null,
  );

  const targetsBrand = normalizeBrandId(plan.brand);
  const targets = useModelPlanTargets(targetsBrand ?? undefined, true);
  const namespaces = targets.data?.namespaces ?? [];
  const floorBySkuId = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const ns of namespaces) {
      for (const sku of ns.skus) map.set(sku.id, sku.sales_floor_multiplier);
    }
    return map;
  }, [namespaces]);

  const entryDrafts = useMemo(
    () =>
      plan.entries.map((entry) => ({
        namespace_id: entry.namespace_id,
        default_rate: String(entry.default_rate_multiplier),
      })),
    [plan.entries],
  );

  const liveBySkuId = useMemo(() => {
    const admittedDrafts = rows
      .filter((row) => row.include)
      .map((row) => ({ sku_id: row.sku.sku_id, rate: row.rate }));
    const resolved = resolvePlanSkuRates(entryDrafts, admittedDrafts, namespaces);
    const map = new Map<string, PlanFloorBreach>();
    for (const breach of liveBreachesFromResolved(resolved)) map.set(breach.sku_id, breach);
    return map;
  }, [rows, entryDrafts, namespaces]);

  function composedResolved(
    admitted: readonly ModelPlanSkuInput[],
  ): readonly SkuResolvedRate[] {
    const drafts = [
      ...plan.skus.map((sku) => ({
        sku_id: sku.sku_id,
        rate: sku.rate_multiplier === null ? '' : String(sku.rate_multiplier),
      })),
      ...admitted.map((sku) => ({
        sku_id: sku.sku_id,
        rate: sku.rate_multiplier === null || sku.rate_multiplier === undefined
          ? ''
          : String(sku.rate_multiplier),
      })),
    ];
    return resolvePlanSkuRates(entryDrafts, drafts, namespaces);
  }

  function submit(): void {
    const admitted: ModelPlanSkuInput[] = [];
    for (const row of rows) {
      if (!row.include) continue;
      const rate = parsePositiveRate(row.rate);
      if (rate === null) {
        setValidationError(t('model_plans.editor.error.rate'));
        return;
      }
      admitted.push({ sku_id: row.sku.sku_id, visible: row.visible, rate_multiplier: rate });
    }
    const resolved = composedResolved(admitted);
    const breaches = liveBreachesFromResolved(resolved);
    if (breaches.length > 0) {
      setBlockingBreaches(breaches);
      setValidationError(null);
      return;
    }
    setValidationError(null);
    onSubmit(admitted, resolved);
  }

  const blocked = blockingBreaches !== null && blockingBreaches.length > 0;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={blocked ? t('model_plans.floor.block_title') : t('model_plans.sync.title', { name: plan.name })}
      closeLabel={t('common.close')}
      size="lg"
      testId="model-plans-sync-dialog"
      footer={
        blocked ? (
          <Button
            variant="primary"
            onClick={() => setBlockingBreaches(null)}
            data-testid="model-plans-floor-block-back"
          >
            {t('model_plans.floor.block_back')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              data-testid="model-plans-sync-submit"
            >
              {t('model_plans.sync.submit')}
            </Button>
          </>
        )
      }
    >
      {blocked && blockingBreaches !== null ? (
        <div className="flex flex-col gap-3" data-testid="model-plans-floor-block">
          <p className="m-0 text-dense text-paper-mid">{t('model_plans.floor.block_body')}</p>
          <FloorBreachesList
            breaches={blockingBreaches}
            testId="model-plans-floor-block-list"
          />
        </div>
      ) : (
      <div className="flex flex-col gap-4">
        <p className="m-0 text-dense text-paper-mid">{t('model_plans.sync.hint')}</p>
        {rows.length === 0 ? (
          <p className="m-0 text-dense text-paper-low" data-testid="model-plans-sync-empty">
            {t('model_plans.sync.empty')}
          </p>
        ) : (
          rows.map((row) => {
            const floor = floorBySkuId.has(row.sku.sku_id)
              ? (floorBySkuId.get(row.sku.sku_id) ?? null)
              : null;
            const rowBreach = row.include ? liveBySkuId.get(row.sku.sku_id) : undefined;
            return (
            <div
              key={row.sku.sku_id}
              className={cx(
                'flex flex-wrap items-end gap-3 rounded-lg border px-3 py-3',
                rowBreach === undefined
                  ? 'border-line-soft bg-well'
                  : 'border-bad-line bg-bad-dim/40',
              )}
              data-testid={`model-plans-sync-row-${row.sku.sku_id}`}
            >
              <Checkbox
                checked={row.include}
                onCheckedChange={(checked) => {
                  setRows((current) =>
                    current.map((item) =>
                      item.sku.sku_id === row.sku.sku_id ? { ...item, include: checked } : item,
                    ),
                  );
                }}
                label={t('model_plans.sync.admit')}
                testId={`model-plans-sync-admit-${row.sku.sku_id}`}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="text-paper">{row.sku.sku_label}</span>
                <span className="font-mono text-micro text-paper-low">
                  {row.sku.namespace_label}
                </span>
                <span
                  className="text-micro text-paper-low"
                  data-testid={`model-plans-sync-floor-${row.sku.sku_id}`}
                >
                  {floor === null
                    ? t('model_plans.editor.sales_floor_unset')
                    : t('model_plans.editor.sales_floor', { floor: fmtRate(floor) })}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={!row.include}
                onClick={() => {
                  setRows((current) =>
                    current.map((item) =>
                      item.sku.sku_id === row.sku.sku_id
                        ? { ...item, visible: !item.visible }
                        : item,
                    ),
                  );
                }}
                data-testid={`model-plans-sync-visible-${row.sku.sku_id}`}
              >
                {row.visible ? (
                  <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                ) : (
                  <EyeOff aria-hidden="true" className="h-3.5 w-3.5" />
                )}
                {row.visible
                  ? t('model_plans.editor.sku_visible')
                  : t('model_plans.editor.sku_hidden')}
              </Button>
              <FormField
                label={t('model_plans.editor.sku_rate')}
                fieldId={`model-plans-sync-rate-${row.sku.sku_id}`}
              >
                <Input
                  id={`model-plans-sync-rate-${row.sku.sku_id}`}
                  mono
                  type="number"
                  step="0.0001"
                  value={row.rate}
                  invalid={rowBreach !== undefined}
                  disabled={!row.include}
                  onChange={(event) => {
                    const next = event.currentTarget.value;
                    setRows((current) =>
                      current.map((item) =>
                        item.sku.sku_id === row.sku.sku_id ? { ...item, rate: next } : item,
                      ),
                    );
                  }}
                  wrapperClassName="w-28"
                  data-testid={`model-plans-sync-rate-${row.sku.sku_id}`}
                />
              </FormField>
              {rowBreach === undefined ? null : (
                <span
                  role="alert"
                  className="w-full text-label text-bad-bright"
                  data-testid={`model-plans-sync-floor-hint-${row.sku.sku_id}`}
                >
                  {floorHintText(rowBreach, t)}
                </span>
              )}
            </div>
            );
          })
        )}
        {validationError !== null ? (
          <p role="alert" className="m-0 text-dense text-bad">
            {validationError}
          </p>
        ) : null}
      </div>
      )}
    </Dialog>
  );
}

export function composeSyncUpdate(
  plan: ModelPlanDetail,
  admitted: readonly ModelPlanSkuInput[],
): UpdateModelPlanRequest {
  return {
    entries: plan.entries.map((entry) => ({
      namespace_id: entry.namespace_id,
      default_rate_multiplier: entry.default_rate_multiplier,
    })),
    skus: [
      ...plan.skus.map((sku) => ({
        sku_id: sku.sku_id,
        visible: sku.visible,
        rate_multiplier: sku.rate_multiplier,
      })),
      ...admitted,
    ],
  };
}
